import { ArrowRight, ChevronRight, Lock, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Poster } from '../components/common';
import {} from '../shared';
import { useSeries } from './context';

/** The pitch, its characters and the controls that develop or revise them. */
export function OverviewTab() {
  const { project: p, pitch, controlsDisabled, revision, setRevision, setTab, run } = useSeries();
  return (
    <div className="overview-columns">
      <div className="overview-art">
        <Poster project={p} />
        <div className="art-caption">
          <span>CONCEPT DIRECTION</span>
          <span>9:16 · Vertical drama</span>
        </div>
      </div>
      <div className="overview-body">
        <div className="panel-title">
          <Sparkles size={18} />
          <h2>{pitch ? 'The pitch' : 'It starts with an idea'}</h2>
          {pitch && <span className="version-tag">v{p.pitchVersion}</span>}
        </div>
        {pitch ? (
          <>
            <p className="pitch-lead">{pitch.dramaticEngine}</p>
            <div className="pitch-facts">
              <div>
                <label>THE CONFLICT</label>
                <p>{pitch.conflict}</p>
              </div>
              <div>
                <label>THE TONE</label>
                <p>{pitch.tone}</p>
              </div>
              <div>
                <label>WHERE IT ENDS</label>
                <p>{pitch.ending}</p>
              </div>
            </div>
            <div className="character-chips">
              {pitch.characters.map((c) => (
                <span key={c.id}>
                  <span className="mini-avatar">{c.name[0]}</span>
                  {c.name}
                  <small>{c.role}</small>
                </span>
              ))}
            </div>
            {!p.castLocked && (
              <>
                <label className="field-label" htmlFor="pitch-revision">
                  Make it yours
                </label>
                <textarea
                  id="pitch-revision"
                  value={revision}
                  onChange={(e) => setRevision(e.target.value)}
                  placeholder="Make it more romantic. Change the ending. Give her something to lose…"
                  rows={3}
                />
                <div className="inline-actions">
                  <Button
                    variant="outline"
                    disabled={controlsDisabled || !revision.trim()}
                    onClick={() => void run('REVISE_PITCH', { instruction: revision })}
                  >
                    <Sparkles size={15} />
                    Revise pitch
                  </Button>
                  <Button
                    disabled={controlsDisabled}
                    onClick={() =>
                      void run('PLAN_SEASON').then((success) => {
                        if (success) setTab('Blueprint');
                      })
                    }
                  >
                    {p.seasonVersion ? 'Replan season' : 'Approve & plan season'}
                    <ArrowRight size={16} />
                  </Button>
                </div>
              </>
            )}
            {p.castLocked && (
              <div className="notice">
                <Lock size={18} />
                <span>
                  Story and cast are locked for this production. Episode revisions preserve these
                  identities.
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="pitch-lead">{p.idea}</p>
            <p className="muted">
              Your showrunner will develop the characters, dramatic engine, and a concise pitch.
              You’ll review it before moving into production.
            </p>
            <Button disabled={controlsDisabled} onClick={() => void run('DEVELOP')}>
              <Sparkles size={16} />
              Develop my series
            </Button>
          </>
        )}
        <div className="process-strip">
          {['Idea', 'Blueprint', 'Cast', 'Produce', 'Review'].map((s, i) => (
            <span
              key={s}
              className={
                i <= ['IDEA', 'PITCH', 'CASTING', 'PRODUCTION', 'REVIEW'].indexOf(p.phase)
                  ? 'complete'
                  : ''
              }
            >
              <i>{i + 1}</i>
              {s}
              {i < 4 && <ChevronRight size={12} />}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
